import { Fragment, useEffect, useState } from "react";

import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { createColumnHelper, flexRender, getCoreRowModel, getExpandedRowModel, getPaginationRowModel, getSortedRowModel, SortingState, useReactTable } from "@tanstack/react-table";

import classNames from "../../utils/classNames";
import PriceFormat from "../../utils/priceFormat";
import openInNewTab from "../../utils/openInNewTab";
import { IInvoiceItem } from "../../models/invoice/IInvoice";
import useInvoiceMutations from "../../data/useInvoiceMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";


export default function InvoiceItemsTable({ 
  data,
  setValue,
  register,
  getValues
}: { data: IInvoiceItem[], setValue: any, register: any, getValues: any }) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const colHelper = createColumnHelper<IInvoiceItem>();
  const [total, setTotal] = useState(0);
  const { saveHandlers } = useDefaultCRUDHandlers("Invoice Item");

  const { updateInvoiceItem } = useInvoiceMutations();

  const calculateTotal = () => {
    return data.reduce((acc, item) => {
      const unitPrice = getValues(`unitPrice-${item.id}`) || 0;
      console.log("Unit Price for item", item.id, ":", unitPrice);
      const quantity = getValues(`quantity-${item.id}`) || item.quantity || 1;
      // const discount = item.discount || 0;
      // const taxRate = item.taxRate || 0;

      // Calculate total for the item
      let itemTotal = unitPrice * quantity;
      // itemTotal -= (discount / 100) * itemTotal; // Apply discount
      // itemTotal += (taxRate / 100) * itemTotal; // Apply tax

      return acc + itemTotal;
    }, 0);
  }

  const updateLineItem = (item: IInvoiceItem) => {
    const formData = {
      ...item,
      unitPrice: getValues(`unitPrice-${item.id}`),
      taxRate: getValues(`taxRate-${item.id}`),
      quantity: getValues(`quantity-${item.id}`),
    }

    updateInvoiceItem.mutate({ id: item.invoiceId, payload: formData }, {
      onSuccess: () => {
        saveHandlers.onSuccess();
        setTotal(calculateTotal());
      }
    });
  }

  useEffect(() => {
    // console.log("Data changed, recalculating total and setting default values");
    for (const item of data) {
      // Set default values for unitPrice and taxRate if they are not set
      if (item.unitPrice === undefined || item.unitPrice === null) {
        setValue(`unitPrice-${item.id}`, "0.00");
      } else {
        setValue(`unitPrice-${item.id}`, item.unitPrice.toFixed(2));
      }

      if (item.taxRate === undefined || item.taxRate === null) {
        setValue(`taxRate-${item.id}`, "0");
      } else {
        setValue(`taxRate-${item.id}`, item.taxRate.toString());
      }

      // do something with the discount
      if (item.discount === undefined || item.discount === null) {
        setValue(`discount-${item.id}`, "0");
      } else {
        setValue(`discount-${item.id}`, item.discount.toString());
      }

      setValue(`quantity-${item.id}`, item.quantity || "1");
    }

    setTotal(calculateTotal());
  }, [data]);

  useEffect(() => {
    console.log("Total updated:", total);
    setValue("itemTotal", total);
  }, [total, setValue]);

  const cols = [
    colHelper.accessor("description", {
      header: "Description",
      sortingFn: "auto",
      cell: (info) => (
        <div>
          <p>{info.getValue()}</p>
          {info.row.original.donorVehicleDescription && (
            <p className="mt-0.5 text-xs text-gray-500">{info.row.original.donorVehicleDescription}</p>
          )}
        </div>
      ),
    }),
    // colHelper.accessor("quantity", {
    //   header: "Quantity",
    //   sortingFn: "auto",
    // }),
    colHelper.accessor("quantity", {
      id: "quantity",
      header: "Quantity",
      enableSorting: true,
      cell: (info) => <span className="flex items-center gap-x-2">
        <input 
          type="number" 
          {...register(`quantity-${info.row.original.id}`)}
          className="w-16"
          min={0}
          onClick={(e) => e.stopPropagation()}
        />
      </span>,
    }),
    colHelper.accessor("unitPrice", {
      id: "unitPrice",
      header: "Unit Price",
      enableSorting: false,
      cell: (info) => <span className="flex items-center gap-x-2">
        £<input 
          type="number" 
          {...register(`unitPrice-${info.row.original.id}`)}
          className="w-24"
          min={0}
          onClick={(e) => e.stopPropagation()}
        />
      </span>,
    }),
    // colHelper.accessor("discount", {
    //   header: "Discount",
    //   sortingFn: "auto",
    // }),

    colHelper.accessor("taxRate", {
      id: "taxRate",
      header: "Tax Rate",
      enableSorting: false,
      cell: (info) => <span className="flex items-center gap-x-2">
        <input 
          type="number" 
          {...register(`taxRate-${info.row.original.id}`)}
          className="w-16"
          min={0}
          onClick={(e) => e.stopPropagation()}
        />%
      </span>,
    }),
     
    colHelper.display({
      id: "actions",
      header: "",
      cell: (info) => (
        <div className="flex gap-x-2">
          <CheckCircleIcon
            className="h-6 w-6 cursor-pointer hover:text-green-600 transition-colors duration-200"
            onClick={(e) => {
              e.stopPropagation();
              updateLineItem(info.row.original);
            }}
          />
        </div>
      ),
    })
  ];

  const table = useReactTable({
    data: data,
    columns: cols,
    state: {
      sorting: sorting,
    },
    enableRowSelection: true,
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="mt-4 bg-white overflow-auto">
      <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="bg-primary-50 text-gray-800">
              {headerGroup.headers.map((header, i) => (
                <th
                  key={header.id}
                  scope="col"
                  className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                >
                  {header.isPlaceholder ? null : (
                    <div
                      {...{
                        className: header.column.getCanSort()
                          ? "cursor-pointer select-none"
                          : "",
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {{
                        asc: " ▲",
                        desc: " ▼",
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row, i) => {
            let rowCells = row.getVisibleCells();
            return (
              <Fragment key={i}>
                <tr
                  key={row.id}
                  onClick={() => {
                    if (row.original.partId) {
                      openInNewTab(`/stock/${row.original.partId}`);
                    }
                  }}
                  className={classNames(
                    "hover:bg-gray-100",
                    row.original.partId 
                    ? "cursor-pointer border-l-primary-500 border-l-4" 
                    : "",
                  )}
                >
                  {rowCells.map((cell) => (
                    <td key={cell.id} className="px-3 py-3 border text-sm">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
      <input type="hidden" {...register("itemTotal")} />
      <small className="text-gray-500 mt-2">
        Total: {PriceFormat(total)}
      </small>
    </div>
  )
}